import { ANNOT_ENDE, SIG_ENDE } from './abgeleitet';

/**
 * Die Sprache der Dateinamen: wie eine Kennung darin steht, wie sie wieder herauskommt, und
 * wie lang das Ganze werden darf.
 *
 * ⚠️ **Der Grund für dieses Modul ist ein Befund vom Gerät (2026-09-21).** Weder Foto noch
 * Video liessen sich ablegen; iOS wies jeden Schreibversuch ab:
 *
 * ```
 * 'writeFile' failed with: The item couldn't be saved because the file name
 * "photo_date=2026-09-21-13-09-53-023_examID=e9f703be-…_candidateID=edb106a3-…" is invalid.
 * ```
 *
 * Das ist `ENAMETOOLONG`. **APFS lässt 255 Bytes je Namensteil**, und der alte Aufbau kam mit
 * sechs UUIDs à 36 Zeichen und ausgeschriebenen Schlüsseln auf 282 (Stationskommentar) bis
 * 330 Zeichen (mit Aufgabe). Beide Gleise waren betroffen: das Gleis tauscht nur die
 * Schlüsselnamen, die Werte sind in beiden Fällen dieselben UUIDs unseres Datenmodells.
 *
 * ⚠️ **Der Name ist reine Hausangelegenheit.** Der Server bekommt die Zuordnung als eigene
 * Felder neben der Datei (`server/anhaenge.ts::baueAnhangFelder`,
 * `server/classic/hochladen.ts::baueFelder`); gelesen wird der Name nur von uns selbst. Er
 * durfte deshalb ohne Absprache kürzer werden — was er weiterhin leisten muss, ist die
 * Vorgabe des Nutzers: **eine Datei sagt auch ohne Datenbank, wozu sie gehört.**
 */

/**
 * Was eine Datei höchstens heissen darf, in Bytes — die Grenze von APFS (und ebenso von
 * ext4). Nicht die Grenze des ganzen Pfades; die liegt bei 1024 und ist hier kein Thema.
 */
export const NAMENSGRENZE = 255;

/**
 * Was für die Anhängsel freibleiben muss.
 *
 * Die längste Kette, die an einen Namen antreten kann, ist die Beglaubigung einer
 * Annotationsebene: `…mp4-annotations.json.sig`. Wer den Grundnamen bis an die Grenze füllt,
 * verschiebt den Fehler nur von der Aufnahme auf ihre Begleitdatei — und die Beglaubigung ist
 * genau das, was nicht fehlen darf.
 */
export const ABLEITUNGS_RESERVE = ANNOT_ENDE.length + SIG_ENDE.length;

/** Wie lang der Grundname werden darf, Endung eingerechnet. */
export const NAME_BUDGET = NAMENSGRENZE - ABLEITUNGS_RESERVE;

let budget = NAME_BUDGET;

/**
 * Was der Grundname gerade höchstens haben darf. Unter Windows enger, wenn der ganze Pfad
 * sonst über 260 Zeichen käme — gesetzt beim Start (`platform/pfadlaenge.ts`).
 */
export const namensBudget = (): number => budget;

export function setzeNamensBudget(n: number): void {
  budget = Math.min(NAME_BUDGET, n);
}

/* --------------------------------------------------------------- Kennungen kompakt */

/**
 * Das Alphabet der kompakten Kennung — Base64 mit zwei getauschten Zeichen.
 *
 * ⚠️ **Kein `_` und kein `+`, kein `/`, kein `=`.** `+` und `/` haben im Dateinamen nichts zu
 * suchen. `_` und `=` sind in unseren Namen die **Trennzeichen**: ein Wert mit `_` darin
 * zerfiele beim Zerlegen in zwei Teile, von denen der zweite mangels `=` stillschweigend
 * übergangen würde — die Kennung wäre abgeschnitten, und niemand sähe es der Datei an. An
 * ihrer Stelle stehen `-` und `~`, beide in Dateinamen unbedenklich und von keinem unserer
 * Leser als Trenner gedeutet.
 */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-~';

const UUID_FORM = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KOMPAKT_FORM = /^[A-Za-z0-9~-]{22}$/;

/** Ist das eine UUID in der ausgeschriebenen Form mit Bindestrichen? */
export const istUuid = (wert: string): boolean => UUID_FORM.test(wert);

/**
 * Eine UUID in 22 Zeichen — dieselben 16 Bytes, nur dichter geschrieben (Base64url ohne die
 * beiden Füllzeichen).
 *
 * ⚠️ **Verlustfrei und umkehrbar.** Keine Kürzung, kein Präfix, keine Prüfsumme: `volleKennung`
 * holt die UUID Zeichen für Zeichen zurück. Eine abgeschnittene Kennung wäre in einer
 * Prüfungsablage nicht vertretbar — zwei Aufnahmen verschiedener Prüflinge dürfen nicht
 * denselben Namen bekommen können.
 *
 * Zum Alphabet s. `ALPHABET` — es meidet die Trennzeichen unserer Namen.
 *
 * Werte, die keine UUID sind, bleiben unberührt: im Classic-Gleis heissen unsere Kennungen
 * `classic:task:123` (`server/classic/kennung.ts`) und sind ohnehin kurz.
 */
export function kompakteKennung(wert: string): string {
  if (!istUuid(wert)) return wert;
  const hex = wert.replace(/-/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < 32; i += 2) bytes.push(parseInt(hex.slice(i, i + 2), 16));

  let aus = '';
  for (let i = 0; i < 15; i += 3) {
    const dreier = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    aus +=
      ALPHABET[(dreier >> 18) & 63] +
      ALPHABET[(dreier >> 12) & 63] +
      ALPHABET[(dreier >> 6) & 63] +
      ALPHABET[dreier & 63];
  }
  // Das sechzehnte Byte bleibt allein übrig: sechs Bits, dann zwei aufgefüllte.
  aus += ALPHABET[(bytes[15] >> 2) & 63] + ALPHABET[(bytes[15] << 4) & 63];
  return aus;
}

/**
 * Der Rückweg: aus 22 Zeichen wieder die ausgeschriebene UUID.
 *
 * Alles, was nicht wie eine kompakte Kennung aussieht, kommt unverändert zurück — ältere
 * Dateien tragen die volle UUID, und Classic-Kennungen tragen Doppelpunkte.
 */
export function volleKennung(wert: string): string {
  if (!KOMPAKT_FORM.test(wert)) return wert;

  const sechser: number[] = [];
  for (const zeichen of wert) {
    const stelle = ALPHABET.indexOf(zeichen);
    if (stelle < 0) return wert;
    sechser.push(stelle);
  }

  const bytes: number[] = [];
  for (let i = 0; i < 20; i += 4) {
    const vierer =
      (sechser[i] << 18) | (sechser[i + 1] << 12) | (sechser[i + 2] << 6) | sechser[i + 3];
    bytes.push((vierer >> 16) & 255, (vierer >> 8) & 255, vierer & 255);
  }
  bytes.push(((sechser[20] << 2) | (sechser[21] >> 4)) & 255);

  const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  return (
    hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' +
    hex.slice(16, 20) + '-' + hex.slice(20)
  );
}

/* ------------------------------------------------------------------ Die Schlüssel */

/**
 * Die kurzen Schlüssel, die seit dem 2026-09-21 geschrieben werden, und wofür sie stehen.
 *
 * Vier Zeichen, weil der Name lesbar bleiben soll: `_cand=` sagt einem Menschen im
 * Dateimanager noch, was dort steht, `_c=` nicht mehr. Die ausgeschriebenen Namen haben
 * zusammen rund vierzig Zeichen gekostet — die fehlten am Ende genau.
 *
 * ⚠️ **Das Gleis bleibt unterscheidbar** (Vorgabe des Nutzers, 2026-09-17): Relay schreibt
 * `cont`/`item`, Classic `stat`/`task`. Gelesen werden weiterhin alle Formen.
 */
export const KURZ_SCHLUESSEL: Record<string, string> = {
  exam: 'examID',
  cand: 'candidateID',
  exmr: 'examinerID',
  actr: 'actorID',
  cont: 'examItemContainerID',
  item: 'examItemID',
  stat: 'stationID',
  task: 'taskID',
};

/** Welche Felder eine Kennung tragen — nur sie werden beim Lesen wieder entfaltet. */
const KENNUNGSFELDER = new Set(Object.keys(KURZ_SCHLUESSEL));

/**
 * Die Felder eines Dateinamens, auf die ausgeschriebenen Schlüssel gebracht und mit vollen
 * Kennungen.
 *
 * ⚠️ **Geschrieben wird eine Form, gelesen werden alle.** Auf dem Gerät liegen nebeneinander:
 * die kurze Form von heute, die lange vom 2026-09-14, die Form ohne `actorID` und ohne Art
 * davor, und nach einem Moduswechsel beide Gleise. Ein Formatwechsel darf keine Aufnahme
 * entwerten, die auf ihre Übertragung wartet.
 *
 * ⚠️ **Teile ohne `=` werden übersprungen.** Der erste Teil ist die Art und trägt keins.
 *
 * Erwartet den Namen **ohne** Anhängsel und ohne Endung — `server/multipart.ts::felderAusName`
 * schneidet beides für alle Leser ab.
 */
export function felderAusTeilen(ohneEndung: string): Record<string, string> {
  const felder: Record<string, string> = {};
  for (const teil of ohneEndung.split('_')) {
    const gleich = teil.indexOf('=');
    if (gleich <= 0) continue;
    const kurz = teil.slice(0, gleich);
    const wert = teil.slice(gleich + 1);
    const lang = KURZ_SCHLUESSEL[kurz];
    if (lang === undefined) {
      // Ein ausgeschriebener Schlüssel aus einer älteren Datei — unverändert übernehmen.
      felder[kurz] = wert;
      continue;
    }
    felder[lang] = KENNUNGSFELDER.has(kurz) ? volleKennung(wert) : wert;
  }
  return felder;
}
